import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import injectSheet from 'react-jss'
import compose from 'recompose/compose'

const style = {
    container: {
        perspective: '2.5em',
        pointerEvents: 'all',
        zIndex: '501',
        height: ({ height }) => height,
        width: ({ width }) => width,
    },
}

const calculateLinearGradient = ({ type, offsetX, offsetY }) => {
    // Offsets ranges differ based on media type (i.e., height/width of the touch tilt element)
    // therefore the linear gradients need to be calculated separately for each shape
    if (type === 'photo' || type === 'video' || type === 'text') {
        return `linear-gradient(${180 - offsetX * 60.8}deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) ${60 -
            offsetY * 20}%, rgba(255, 255, 255, 0.25) 100%, transparent 100%)`
    } else if (type === 'panorama') {
        return `linear-gradient(${180 - offsetX * 35.4}deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) ${60 -
            offsetY * 16.4}%, rgba(255, 255, 255, 0.25) 100%, transparent 100%)`
    }
}

class TouchTiltElement extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            style: {},
            offsetY: undefined,
            offsetX: undefined,
            topVal: undefined,
            widthVal: undefined,
            leftVal: undefined,
            heightVal: undefined,
        }
    }

    componentDidMount() {
        this._setStyle(0, 0)
    }

    handleHoverOn = e => {
        const { onHover } = this.props
        onHover && onHover()
        this._setStyle(0, 0)
    }

    handleHoverOff = () => {
        const { onHoverExit, isVisible } = this.props
        if (isVisible && onHoverExit) {
            onHoverExit()
        }
        this._setStyle(0, 0)
    }

    handleMouseMove = e => {
        const { clientX, clientY, target } = e
        let { topVal, widthVal, leftVal, heightVal } = this.state

        // The transform animation changes the values yielded by getBoundingClientRect
        // This results in erratic animations in long or tall elements, such as the Pano
        // To avoid this we have to get the values once on mount, set them on state, then rely on those values
        if (!(topVal && widthVal && leftVal && heightVal)) {
            const { top, width, left, height, ...rest } = target.getBoundingClientRect()
            this.setState({
                topVal: top,
                widthVal: width,
                leftVal: left,
                heightVal: height,
            })
            topVal = top
            widthVal = width
            leftVal = left
            heightVal = height
        }

        const { onHover } = this.props

        const offsetX = (leftVal + widthVal / 2 - clientX) / 200
        const offsetY = (topVal + heightVal / 2 - clientY) / 200
        this._setStyle(offsetY, offsetX)

        onHover && onHover()
    }

    _setStyle(offsetY, offsetX) {
        const { rotationCoefficient, boxShadowDisplacementCoefficient, maxDistortion, height } = this.props
        let style = { height }

        //enforce a limits on the distortion
        let xVal = offsetY
        let yVal = -offsetX
        if (xVal > maxDistortion) xVal = maxDistortion
        if (xVal < -maxDistortion) xVal = -maxDistortion

        if (yVal > maxDistortion) yVal = maxDistortion
        if (yVal < -maxDistortion) yVal = -maxDistortion

        var transformStyle = `
      rotateX(${xVal}deg)
      rotateY(${yVal}deg)
      translate3d(0px, 0px, 0px)
    `

        var boxShadowStyle = `
      ${offsetX * (boxShadowDisplacementCoefficient || 15)}px ${offsetY *
            (boxShadowDisplacementCoefficient || 15)}px 40px rgba(0,0,0,0.5)
    `
        style.transform = transformStyle
        style.boxShadow = boxShadowStyle
        this.setState({ style, offsetY, offsetX })
    }

    render() {
        const { classes, onClick, children, type } = this.props
        const { style, offsetX, offsetY } = this.state
        const childrenWithInjectedOffset = React.Children.map(children, child =>
            React.cloneElement(child, {
                offsetX,
                offsetY,
            })
        )
        return (
            <div
                className={classes.container}
                onClick={onClick}
                onMouseEnter={this.handleHoverOn}
                onMouseMove={this.handleMouseMove}
                onMouseLeave={this.handleHoverOff}
            >
                <TiltedElement offsetX={offsetX} offsetY={offsetY} type={type} {...style}>
                    {childrenWithInjectedOffset}
                </TiltedElement>
            </div>
        )
    }
}

TouchTiltElement.propTypes = {
    id: PropTypes.number,
    onHover: PropTypes.func,
    onClick: PropTypes.func,
}

TouchTiltElement.defaultProps = {
    maxDistortion: 0.25,
}

const tiltedStyle = theme => ({
    tilted: {
        transform: ({ transform }) => transform,
        boxShadow: ({ boxShadow }) => boxShadow,
        transitionProperty: 'transform, box-shadow',
        transitionDuration: '0.2s',
        transitionTimingFunction: 'linear',
        transitionDelay: '0s, -0.5s',
        height: '100%',
        width: '100%',
        position: ({ type }) => ((type === 'photo' || type === 'video') && 'absolute') || 'relative',
        top: 0,
    },
    tiltedOpacityFilter: {
        height: '100%',
        width: '100%',
        backgroundColor: '#000000',
        opacity: 0.15,
        zIndex: '400',
        position: ({ type }) => ((type === 'photo' || type === 'video') && 'absolute') || 'relative',
        top: ({ height, type }) => ((type === 'photo' || type === 'video') && '0') || `-${height}px`,
    },
    tiltedGlareFilter: {
        height: '100%',
        width: '100%',
        zIndex: '500',
        position: ({ type }) => ((type === 'photo' || type === 'video') && 'absolute') || 'relative',
        top: ({ height, type }) => ((type === 'photo' || type === 'video') && '0') || `-${2 * height}px`,
        backgroundImage: ({ offsetX, offsetY, type }) => calculateLinearGradient({ offsetX, offsetY, type }),
    },
})
const TiltedElement = injectSheet(tiltedStyle)(({ classes, children }) => (
    <div className={classes.tilted}>
        {children}
        <div className={classes.tiltedOpacityFilter} />
        <div className={classes.tiltedGlareFilter} />
    </div>
))

export default compose(injectSheet(style))(TouchTiltElement)
